import type { CreditBreakdown } from "@/lib/credit-rounding";

/**
 * Reusable breakdown card shown beneath credit top-up amount inputs.
 * Always renders the same numbers the server will validate against
 * (via the shared `computeCreditBreakdown` helper).
 */
export function CreditBreakdownPanel({ breakdown }: { breakdown: CreditBreakdown }) {
  const fmt = (n: number) => `R${n.toFixed(2)}`;

  if (!breakdown.meetsMinimum) {
    return (
      <div
        data-testid="credit-breakdown"
        className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
      >
        Minimum top-up is <strong>{fmt(breakdown.minTotalZar)}</strong>{" "}
        ({breakdown.minCredits} credits + {fmt(breakdown.adminFeeZar)} admin fee).
      </div>
    );
  }

  return (
    <div
      data-testid="credit-breakdown"
      className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground"
    >
      <div className="font-semibold text-navy">Rounding breakdown</div>
      <dl className="mt-1 grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5">
        <dt>You entered</dt>
        <dd className="text-right tabular-nums">{fmt(breakdown.enteredAmount)}</dd>
        <dt>Admin fee</dt>
        <dd className="text-right tabular-nums">− {fmt(breakdown.adminFeeZar)}</dd>
        <dt>Spend on credits</dt>
        <dd className="text-right tabular-nums">
          {fmt(Math.max(0, breakdown.enteredAmount - breakdown.adminFeeZar))}
        </dd>
        <dt>÷ {fmt(breakdown.unitPriceZar)} per credit, rounded up</dt>
        <dd className="text-right tabular-nums">{breakdown.credits} credits</dd>
        <dt>Credits value</dt>
        <dd className="text-right tabular-nums">{fmt(breakdown.creditsValueZar)}</dd>
        <dt>+ Admin fee</dt>
        <dd className="text-right tabular-nums">{fmt(breakdown.adminFeeZar)}</dd>
        <dt className="border-t border-border pt-1 font-semibold text-navy">
          Total charged at PayFast
        </dt>
        <dd className="border-t border-border pt-1 text-right font-semibold tabular-nums text-navy">
          {fmt(breakdown.totalZar)}
        </dd>
        <dt>You receive</dt>
        <dd className="text-right font-semibold tabular-nums text-navy">
          {breakdown.credits} credits
        </dd>
      </dl>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Credits round up so your spend always covers a whole number of credits.
        The final PayFast charge is recomputed from the credit count, never your
        raw input, so what you pay matches what you receive exactly.
      </p>
    </div>
  );
}
